<template>
  <div class="payroll-metadata-form">
    <oxd-text tag="h6" class="gsl-hrm-main-title">
      {{ $t('pim.payroll_details') }}
    </oxd-text>
    <oxd-divider />
    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="selectedCountry"
              type="select"
              label="Country"
              :options="countryOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <br />
      <div v-if="payrollFields?.length">
        <oxd-form-row>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item v-for="field in payrollFields" :key="field.key">
              <oxd-input-field
                :key="field.key + '_' + rules[field.key]?.length"
                v-model="payrollData[field.key]"
                :label="field.label"
                :required="field.required"
                :rules="rules[field.key]"
                :disabled="!$can.update(`salary_details`)"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
      </div>
      <oxd-form-actions>
        <submit-button
          v-if="payrollFields?.length && $can.update(`salary_details`)"
        />
      </oxd-form-actions>
    </oxd-form>
  </div>
</template>

<script>
import {required} from '@ohrm/core/util/validation/rules';
import {APIService} from '@ohrm/core/util/services/api.service';

export default {
  name: 'ProfilePayrollDetails',
  props: {
    employeeId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.employeeId}/payrolls`,
    );
    return {
      http,
    };
  },
  data() {
    return {
      isLoading: false,
      selectedCountry: null,
      payrollData: {},
      rules: {},
      countryOptions: [
        {label: 'United Kingdom', value: 'UK', id: 'UK'},
        {label: 'India', value: 'IN', id: 'IN'},
        {label: 'United States', value: 'US', id: 'US'},
        {label: 'Germany', value: 'DE', id: 'DE'},
        {label: 'Singapore', value: 'SG', id: 'SG'},
        {label: 'Generic', value: 'XX', id: 'XX'},
      ],
      countryFields: {
        IN: [
          {
            key: 'in_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {
            key: 'in_ifsc_code',
            label: 'IFSC Code',
            required: true,
            rules: {
              pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
              message: 'Invalid IFSC Number',
            },
          },
          {
            key: 'in_pan_number',
            label: 'PAN Number',
            required: true,
            rules: {
              pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
              message: 'Invalid PAN format',
            },
          },
          {
            key: 'in_aadhaar_number',
            label: 'Aadhaar Number',
            required: false,
            rules: {
              pattern: /^\d{12}$/,
              message: 'Aadhaar Number must be 12 digits',
            },
          },

          {key: 'in_epf_uan', label: 'EPF UAN', required: false},
          {key: 'in_esic_number', label: 'ESIC Number', required: false},
        ],
        UK: [
          {
            key: 'uk_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'uk_sort_code', label: 'Sort Code', required: true},
          {
            key: 'uk_ni_number',
            label: 'National Insurance Number',
            required: true,
            rules: {
              pattern: /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/,
              message: 'Invalid NI Number format',
            },
          },
          {key: 'uk_tax_code', label: 'Tax Code', required: true},

          {
            key: 'uk_pension_scheme',
            label: 'Pension Scheme Reference',
            required: false,
          },

          {
            key: 'uk_student_loan_plan',
            label: 'Student Loan Plan Type',
            required: false,
          },
        ],
        US: [
          {
            key: 'us_bank_routing_number',
            label: 'Bank Routing Number',
            required: true,
            rules: {
              pattern: /^\d{9}$/,
              message: 'Routing number must be 9 digits',
            },
          },
          {
            key: 'us_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'us_state_tax_code', label: 'State Tax Code', required: false},

          {
            key: 'us_ssn',
            label: 'Social Security Number',
            required: true,
            rules: {
              pattern: /^\d{3}-\d{2}-\d{4}$/,
              message: 'Invalid SSN (e.g. 123-45-6789)',
            },
          },
          {
            key: 'us_federal_tax_status',
            label: 'Federal Tax Filing Status',
            required: true,
          },

          {
            key: 'us_retirement_plan',
            label: '401(k)/Retirement Plan Contribution',
            required: false,
          },
        ],
        DE: [
          {
            key: 'sg_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {
            key: 'de_iban',
            label: 'IBAN',
            required: true,
            rules: {
              pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/,
              message: 'Invalid IBAN format',
            },
          },
          {
            key: 'bic',
            label: 'BIC/SWIFT Code',
            required: true,
            rules: {
              pattern: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
              message: 'Invalid BIC/SWIFT Code',
            },
          },
          {
            key: 'de_steuer_id',
            label: 'Tax Identification Number (Steuer-ID)',
            required: true,
            rules: {
              pattern: /^\d{11}$/,
              message: 'Steuer-ID must be 11 digits',
            },
          },
          {
            key: 'de_social_security_number',
            label: 'Social Security Number',
            required: true,
          },
          {
            key: 'de_health_insurance',
            label: 'Health Insurance Provider',
            required: false,
          },
        ],
        SG: [
          {
            key: 'sg_nric',
            label: 'NRIC/FIN Number',
            required: true,
            rules: {
              pattern: /^[STFG]\d{7}[A-Z]$/,
              message: 'Invalid NRIC/FIN format',
            },
          },
          {
            key: 'sg_cpf_number',
            label: 'CPF Contribution Number',
            required: true,
          },
        ],
        XX: [
          {
            key: 'xx_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {
            key: 'xx_iban',
            label: 'IBAN',
            required: false,
            rules: {
              pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/,
              message: 'Invalid IBAN format',
            },
          },
          {
            key: 'xx_swift_code',
            label: 'SWIFT/BIC Code',
            required: false,
            rules: {
              pattern: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
              message: 'Invalid SWIFT code',
            },
          },
          {
            key: 'xx_tax_id',
            label: 'Tax Identification Number (TIN)',
            required: true,
          },
          {
            key: 'xx_national_id',
            label: 'National ID / Social Security Number',
            required: true,
          },
        ],
      },
    };
  },

  computed: {
    payrollFields() {
      return this.countryFields[this.selectedCountry?.value] || [];
    },
  },

  watch: {
    selectedCountry(newVal) {
      if (!newVal?.value) return;
      this.isLoading = true;

      this.http
        .getAll({countryCode: newVal?.value})
        .then((response) => {
          const items = response.data?.data || [];

          // Filter fields for selected country
          const fields = this.countryFields[newVal.value] || [];
          const newPayrollData = {};
          const newRules = {};

          fields.forEach((field) => {
            // Try to find saved value for this field
            const item = items.find(
              (i) => i.countryCode === newVal.value && i.fieldKey === field.key,
            );
            newPayrollData[field.key] = item ? item.fieldValue : '';

            // Build validation rules
            const fieldRules = [];

            if (field.required) {
              fieldRules.push(required);
            }
            if (field.rules?.pattern) {
              fieldRules.push((value) => {
                const {pattern, message} = field.rules;
                return (
                  !value || pattern.test(value) || message || 'Invalid format'
                );
              });
            }
            newRules[field.key] = fieldRules;
          });

          this.payrollData = newPayrollData;
          this.rules = newRules;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  beforeMount() {
    this.isLoading = true;
    this.http
      .getAll()
      .then((response) => {
        const items = response.data?.data || [];

        if (items.length) {
          const countryCode = items[0].countryCode;

          // Set selectedCountry from options
          this.selectedCountry = this.countryOptions.find(
            (c) => c.value === countryCode,
          );

          // Prepare payrollData
          const data = {};
          items.forEach((item) => {
            data[item.fieldKey] = item.fieldValue;
          });

          this.payrollData = data;
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            data: {
              country_code: this.selectedCountry?.value,
              fields: this.payrollData,
            },
          },
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .finally(() => {
          this.isLoading = false;
        });
      console.log('Saving payroll data:', {
        country_code: this.selectedCountry?.value,
        fields: this.payrollData,
      });
    },
  },
};
</script>
